import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Select,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  HStack,
  useToast,
  useColorModeValue,
  Box,
  Heading,
  VStack,
  Flex,
} from '@chakra-ui/react';
import api from '../api';

const InvoiceSupplierSelector = ({ suppliers, selectedSupplier, setSelectedSupplier, fetchSuppliers }) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [newSupplierData, setNewSupplierData] = useState({
    name: '',
    email: '',
    phone_number: '',
    edrpou_code: '',
    address: '',
    bank_name: '',
    bank_number: '',
  });
  const toast = useToast();

  const handleNewSupplierChange = (e) => {
    const { name, value } = e.target;
    setNewSupplierData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateNewSupplier = async () => {
    try {
      await api.post('/suppliers/', newSupplierData);
      toast({
        title: 'Постачальник створений!',
        description: `Постачальник ${newSupplierData.name} успішно створено.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsAddSupplierModalOpen(false);
      setNewSupplierData({
        name: '',
        email: '',
        phone_number: '',
        edrpou_code: '',
        address: '',
        bank_name: '',
        bank_number: '',
      });
      fetchSuppliers();
    } catch (error) {
      toast({
        title: 'Помилка!',
        description: 'Не вдалося створити постачальника.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <FormControl mt="4">
        <HStack>
          <Select
            placeholder="Оберіть постачальника"
            value={selectedSupplier || ''}
            onChange={(e) => setSelectedSupplier(parseInt(e.target.value, 10))}
          >
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </Select>
          <Button 
            colorScheme="blue" 
            onClick={() => setIsAddSupplierModalOpen(true)}
            size="sm"
            px={3}
            minW="auto"
          >
            ➕
          </Button>
        </HStack>
      </FormControl>

      <Modal isOpen={isAddSupplierModalOpen} onClose={() => setIsAddSupplierModalOpen(false)} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent 
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="hidden"
          maxH="80vh"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="6px"
            bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)"
          />
          
          <ModalHeader 
            pb={4} 
            pt={8}
            bg={cardBg}
            borderTopRadius="2xl"
          >
            <Heading 
              size="lg"
              bgGradient="linear(to-r, #667eea, #764ba2)"
              bgClip="text"
              fontWeight="bold"
            >
              🏭 Додати постачальника
            </Heading>
          </ModalHeader>
          <ModalCloseButton 
            top={6}
            right={6}
            size="lg"
            borderRadius="full"
            _hover={{
              bg: hoverBg,
              color: textColor,
            }}
          />
          <ModalBody bg={cardBg} pb={6}>
            <Flex gap={8}>
              {/* Left Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 Назва компанії
                    </FormLabel>
                    <Input 
                      name="name" 
                      value={newSupplierData.name} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📧 Email
                    </FormLabel>
                    <Input 
                      name="email" 
                      value={newSupplierData.email} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📞 Номер телефону
                    </FormLabel>
                    <Input 
                      name="phone_number" 
                      value={newSupplierData.phone_number} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 ЄДРПОУ
                    </FormLabel>
                    <Input 
                      name="edrpou_code" 
                      value={newSupplierData.edrpou_code} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Right Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📍 Адреса
                    </FormLabel>
                    <Input 
                      name="address" 
                      value={newSupplierData.address} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏦 Назва банку
                    </FormLabel>
                    <Input 
                      name="bank_name" 
                      value={newSupplierData.bank_name} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      💳 Номер рахунку
                    </FormLabel>
                    <Input 
                      name="bank_number" 
                      value={newSupplierData.bank_number} 
                      onChange={handleNewSupplierChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter 
            bg={cardBg}
            borderBottomRadius="2xl"
            p={6}
          >
            <Button 
              bg="linear-gradient(135deg, #667eea, #764ba2)"
              color="white"
              size="lg"
              mr={4} 
              onClick={handleCreateNewSupplier}
              borderRadius="xl"
              px={8}
              boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
              _hover={{
                bg: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.6)",
              }}
              _active={{
                transform: "translateY(0)",
              }}
            >
              🏭 Зберегти
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setIsAddSupplierModalOpen(false)}
              size="lg"
              borderRadius="xl"
              px={8}
              color={textColor}
              _hover={{
                bg: hoverBg,
              }}
            >
              ❌ Скасувати
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default InvoiceSupplierSelector;
